import React, { useEffect, useRef, useState } from 'react';
import Stack from 'atoms/Layout/Stack/Stack';
import Typography from 'atoms/Typography/Typography';
import Chevron from 'src/app/icons/Monospaced/Chevron/Chevron';
import Globe02 from 'src/app/icons/Monospaced/Globe/Globe02';
import { useI18nContext, Language } from 'src/hooks/useI18nContext';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, getBasePathWithoutLanguage } from 'src/utils/i18n-config';
import { cssVar } from 'styles/helper';
import {
  LanguageSelectorContainer,
  LanguageSelectorButton,
  LanguageDropdownList,
  LanguageDropdownContent,
  LanguageOptionButton,
} from './LanguageSelector.styles';

const LanguageSelector = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionListRef = useRef<HTMLDivElement>(null);
  const { language: currentLanguage } = useI18nContext();

  // Handle dropdown animation
  useEffect(() => {
    if (!optionListRef.current) return;

    if (isOpen) {
      optionListRef.current.style.maxHeight = optionListRef.current.scrollHeight + 10 + 'px';
      optionListRef.current.style.opacity = '1';
    } else {
      optionListRef.current.style.maxHeight = '0px';
      optionListRef.current.style.opacity = '0';
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getLocalizedPath = (targetLanguage: Language): string => {
    if (typeof window === 'undefined') return '/';

    let basePath = getBasePathWithoutLanguage(window.location.pathname);

    // Ensure basePath ends without trailing slash unless it's root
    if (basePath !== '/' && basePath.endsWith('/')) {
      basePath = basePath.slice(0, -1);
    }

    // Add new language prefix (except for English)
    if (targetLanguage === 'en') {
      return basePath || '/';
    }

    return `/${targetLanguage}${basePath}`;
  };

  const handleLanguageChange = (targetLanguage: Language) => {
    if (targetLanguage === currentLanguage) {
      setIsOpen(false);
      return;
    }

    const newPath = getLocalizedPath(targetLanguage);
    window.location.href = newPath;
  };

  return (
    <LanguageSelectorContainer ref={dropdownRef}>
      <LanguageSelectorButton
        variant='tertiary'
        size='md'
        onClick={() => setIsOpen(!isOpen)}
        icon={
          <Globe02
            width={18}
            color='var(--text-primary-on-brand)'
          />
        }
        iconPosition='left'
      >
        <Stack
          direction='row'
          gap='spacing-xs'
          align='center'
        >
          <Typography
            type='paragraph'
            size='sm'
            color='text-primary-on-brand'
            fontWeight='font-weight-medium'
          >
            {LANGUAGE_NAMES[currentLanguage]}
          </Typography>
          <Chevron
            orientation={isOpen ? 'up' : 'down'}
            width={14}
            color='var(--text-primary-on-brand)'
          />
        </Stack>
      </LanguageSelectorButton>

      <LanguageDropdownList ref={optionListRef}>
        <LanguageDropdownContent
          border={`1px solid ${cssVar('border-secondary-alt')}`}
          borderRadius='radius-md'
        >
          <Stack
            direction='column'
            padding={['spacing-sm', 'spacing-none']}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <LanguageOptionButton
                key={lang}
                $isActive={lang === currentLanguage}
                onClick={() => handleLanguageChange(lang)}
              >
                <Typography
                  type='paragraph'
                  size='sm'
                  color='text-primary-on-brand'
                  fontWeight='font-weight-medium'
                >
                  {LANGUAGE_NAMES[lang]}
                </Typography>
              </LanguageOptionButton>
            ))}
          </Stack>
        </LanguageDropdownContent>
      </LanguageDropdownList>
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;
